"use client";

import {
  buildLoginRedirectUrl,
  buildRedirectUrlFromParts,
} from "@/lib/utils/auth-redirect";
import { Text, TextLink, Strong } from "@/components/catalyst/text";
import { usePathname, useSearchParams } from "next/navigation";
import { Field, Label } from "@/components/catalyst/fieldset";
import { Button } from "@/components/catalyst/button";
import { Input } from "@/components/catalyst/input";
import { authClient } from "@/lib/auth/client";
import { useTranslations } from "next-intl";
import { BASE_URL } from "@/lib/constants";
import { cn } from "@/lib/utils/classes";
import { useState } from "react";

type CheckoutEmailVerificationProps = {
  onVerified?: () => void;
};

export function CheckoutEmailVerification({
  onVerified,
}: CheckoutEmailVerificationProps) {
  const t = useTranslations("CheckoutForm");
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const fullPath = buildRedirectUrlFromParts(pathname, searchParams.toString());
  const loginUrl = buildLoginRedirectUrl(fullPath);
  const fullUrl = `${BASE_URL}${fullPath}`;

  const [isVerifying, setIsVerifying] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleVerifyEmail = async () => {
    if (!email) {
      setMessage({ type: "error", text: t("emailRequired") });
      return;
    }

    setIsVerifying(true);
    setMessage(null);

    try {
      const { error } = await authClient.signIn.magicLink({
        email,
        name: name || undefined,
        callbackURL: fullUrl,
        errorCallbackURL: fullUrl,
      });

      if (error) {
        setMessage({ type: "error", text: error.message || t("verifyError") });
      } else {
        setMessage({
          type: "success",
          text: t("verifyEmailSent"),
        });
        onVerified?.();
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : t("verifyError"),
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-4 rounded-md border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
      <Text className="text-sm font-medium">{t("verifyEmailTitle")}</Text>
      <Text className="text-xs text-zinc-600 dark:text-zinc-400">
        {t("verifyEmailDescription")}
      </Text>
      <Text className="text-xs text-zinc-600 dark:text-zinc-400">
        {t("alreadyHaveAccount")}{" "}
        <TextLink href={loginUrl}>
          <Strong>{t("signIn")}</Strong>
        </TextLink>
      </Text>
      <div className="space-y-3">
        <Field>
          <Label>{t("name")}</Label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            autoComplete="name"
            data-testid="checkout-name-input"
          />
        </Field>
        <Field>
          <Label>{t("email")}</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            autoComplete="email"
            required
            data-testid="checkout-email-input"
          />
        </Field>
        {message && (
          <div
            className={cn(
              "rounded-md border p-3",
              message.type === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-800",
            )}
            data-testid={`checkout-email-verification-message-${message.type}`}
          >
            <p className="text-sm">{message.text}</p>
          </div>
        )}
        <Button
          type="button"
          onClick={handleVerifyEmail}
          disabled={isVerifying || !email}
          className="w-full"
          color="purple"
          data-testid="checkout-verify-email-button"
        >
          {isVerifying ? (
            <div className="flex items-center justify-center">
              <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
              {t("sending")}
            </div>
          ) : (
            t("verifyEmail")
          )}
        </Button>
      </div>
    </div>
  );
}
